// In-call chat sidebar (WhatsApp-style). Backed by the platform's persisted
// chat: it loads history and sends via the REST APIs (getChatMessages /
// sendMessage) so every message is stored in `chats` / `chat_messages`, and it
// mirrors the normal-chat realtime path over the socket (`send-message` out,
// `receive-message` in) so the user and lawyer stay in sync live during the call.
// The call chat IS their normal chat (same lawyer_id + user_id), so anything
// said here also shows up in their regular chat history. Supports file uploads.
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import SocketService from "../../services/SocketService";

const API = process.env.REACT_APP_API_URL;

// Map a picked File's mime type to the backend's file_type enum.
const mimeToFileType = (file) => {
  const t = (file.type || "").toLowerCase();
  if (t.startsWith("image/")) return "image";
  if (t.startsWith("video/")) return "video";
  if (t.startsWith("audio/")) return "audio";
  return "doc";
};

// Normalize a ChatMessage (from REST or socket) into the render shape.
const normalize = (m) => ({
  id: m.id ?? `${Date.now()}-${Math.random()}`,
  sender: m.sender === "lawyer" ? "lawyer" : "user",
  text: m.message || "",
  file: m.file || null,
  fileType: m.file_type || "",
  ts: m.created_at ? new Date(m.created_at).getTime() : Date.now(),
});

const fileName = (url) => {
  try {
    return decodeURIComponent(url.split("/").pop());
  } catch (e) {
    return "Attachment";
  }
};

export default function ChatPanel({ call, open, onClose, onIncoming }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);
  const fileRef = useRef(null);
  // { id, user_id, lawyer_id } — the chat row this call maps to. Resolved from
  // getChatMessages (or the first sent message) and used for socket routing.
  const chatRef = useRef(null);

  // The OTP-verified user token persisted by ConsultationCall; also passed on
  // the call object as a fallback.
  const token = call?.authToken || localStorage.getItem("cc_auth_token");
  const lawyerId = call?.lawyer?.id;
  const userId = call?.user?.id;

  // ---- Load persisted history + resolve the chat id for this pair ----------
  useEffect(() => {
    if (!token || !lawyerId) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/getChatMessages`, {
        params: { lawyer_id: lawyerId },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (cancelled || !res?.data?.status) return;
        const d = res.data.data || {};
        const chat = d.chat || {};
        chatRef.current = { id: chat.id, user_id: userId, lawyer_id: lawyerId };
        // API returns newest-first (paginated) — show oldest-first.
        const list = (d.messages || []).slice().reverse().map(normalize);
        setMessages(list);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, lawyerId, userId]);

  // ---- Receive the lawyer's messages relayed over the socket ---------------
  useEffect(() => {
    const unsub = SocketService.subscribe("receive-message", (message, chat) => {
      const cid = chatRef.current?.id;
      const incomingChatId = chat?.id ?? message?.chat_id;
      // Only messages for this call's chat, and never our own echo.
      if (cid && incomingChatId && String(incomingChatId) !== String(cid)) return;
      if (message?.sender === "user") return;
      setMessages((prev) => {
        if (message?.id && prev.some((m) => String(m.id) === String(message.id))) return prev;
        return [...prev, normalize(message)];
      });
      if (!open && onIncoming) onIncoming();
    });
    return () => unsub && unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep the list pinned to the newest message.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open]);

  // Persist a message, append it locally, then relay it to the lawyer live.
  const postMessage = async (form) => {
    const res = await axios.post(`${API}/sendMessage`, form, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
    });
    if (res?.data?.status && res.data.data) {
      const message = res.data.data;
      if (!chatRef.current?.id && message.chat_id) {
        chatRef.current = { id: message.chat_id, user_id: userId, lawyer_id: lawyerId };
      }
      setMessages((prev) => [...prev, normalize(message)]);
      const chat = chatRef.current;
      if (chat?.id) SocketService.emit("send-message", message, chat);
    }
  };

  const sendText = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body || sending || !token) return;
    setSending(true);
    try {
      const form = new FormData();
      if (chatRef.current?.id) form.append("chat_id", chatRef.current.id);
      else form.append("lawyer_id", lawyerId);
      form.append("message", body);
      await postMessage(form);
      setText("");
    } catch (e) {
      /* keep the text so the user can retry */
    } finally {
      setSending(false);
    }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file || sending || !token) return;
    setSending(true);
    try {
      const form = new FormData();
      if (chatRef.current?.id) form.append("chat_id", chatRef.current.id);
      else form.append("lawyer_id", lawyerId);
      form.append("file", file);
      form.append("file_type", mimeToFileType(file));
      await postMessage(form);
    } catch (e) {
      /* ignore — user can retry */
    } finally {
      setSending(false);
    }
  };

  const fmtTime = (ts) =>
    new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const renderFile = (m) => {
    if (!m.file) return null;
    if (m.fileType === "image") {
      return (
        <a href={m.file} target="_blank" rel="noreferrer" className="lp-msg-img-link">
          <img src={m.file} alt="attachment" className="lp-msg-img" />
        </a>
      );
    }
    return (
      <a href={m.file} target="_blank" rel="noreferrer" className="lp-msg-file">
        <i className="bi bi-file-earmark-arrow-down" />
        <span className="lp-msg-file-name">{fileName(m.file)}</span>
      </a>
    );
  };

  return (
    <aside className={open ? "lp-chat-panel lp-chat-open" : "lp-chat-panel"}>
      <div className="lp-chat-head">
        <div className="lp-chat-head-info">
          <div className="lp-chat-avatar">
            {call?.lawyer?.picture ? (
              <img src={call.lawyer.picture} alt={call?.lawyer?.name} />
            ) : (
              <i className="bi bi-person-fill" />
            )}
          </div>
          <div>
            <div className="lp-chat-title">{call?.lawyer?.name || "Lawyer"}</div>
            <div className="lp-chat-sub">Chat during your call</div>
          </div>
        </div>
        <button type="button" className="lp-chat-close" onClick={onClose} aria-label="Close chat">
          <i className="bi bi-x-lg" />
        </button>
      </div>

      <div className="lp-chat-body" ref={listRef}>
        {loading ? (
          <div className="lp-chat-empty">
            <i className="bi bi-arrow-repeat lp-chat-spin" />
            <span>Loading messages…</span>
          </div>
        ) : messages.length === 0 ? (
          <div className="lp-chat-empty">
            <i className="bi bi-chat-dots" />
            <span>No messages yet. Say hello 👋</span>
          </div>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={m.from === "user" || m.sender === "user" ? "lp-msg lp-msg-out" : "lp-msg lp-msg-in"}>
              <div className="lp-msg-bubble">
                {renderFile(m)}
                {m.text ? <span className="lp-msg-text">{m.text}</span> : null}
                <span className="lp-msg-time">{fmtTime(m.ts)}</span>
              </div>
            </div>
          ))
        )}
      </div>

      <form className="lp-chat-input" onSubmit={sendText}>
        <input
          type="file"
          ref={fileRef}
          onChange={onPickFile}
          hidden
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
        />
        <button
          type="button"
          className="lp-chat-attach"
          onClick={() => fileRef.current?.click()}
          disabled={sending}
          aria-label="Attach file"
        >
          <i className="bi bi-paperclip" />
        </button>
        <input
          type="text"
          placeholder="Type a message"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <button type="submit" aria-label="Send" disabled={!text.trim() || sending}>
          <i className="bi bi-send-fill" />
        </button>
      </form>
    </aside>
  );
}
